import { Component, Injector } from '@angular/core';
import {ParamMap} from '@angular/router';
import {forkJoin, Observable} from 'rxjs';
import {mergeMap} from 'rxjs/operators';
import { BankMovementInfo, OrderInfo, PaymentInfo} from 'src/app/models/models';
import { Bank_Movement_Order, Payment } from 'src/app/models/RestModels';
import { SearchObject,RestResponse } from 'src/app/services/Rest';
import {BaseComponent} from '../base/base.component';
import { PrinterService } from 'src/app/services/printer.service';
import {
	SingleOrderReceiptData,
	ensurePosPrinterLoaded,
	paymentTypeCode,
	resolveTicketForUsbPrint,
	storeSnapshot
} from 'src/app/classes/PaymentReceiptTicketBuilder';

interface Bank_Movement_Order_Custom extends Bank_Movement_Order
{
	sum: number;
	balance: number;
	payment_type: string;
}

@Component({
	selector: 'app-print-payment-receipt',
	templateUrl: './print-payment-receipt.component.html',
	styleUrls: ['./print-payment-receipt.component.css']
})
export class PrintPaymentReceiptComponent extends BaseComponent
{
	private printerService: PrinterService;

	constructor(public injector: Injector) {
		super(injector);
		this.printerService = injector.get(PrinterService);
	}

	payment_info:PaymentInfo | null = null;
	orders_by_id:Record<number,OrderInfo> = {};
	payment_info_list:PaymentInfo[] = [];
	order_info:OrderInfo | null = null;
	sums:Bank_Movement_Order_Custom[] = [];
	balance:number = 0;
	all_partial_payments:number = 0;

	legend:Record<string,string> = {
		'CASH-MXN':'Efect.',
		'CASH-USD':'Dlls.',
		'CREDIT_CARD-MXN':'T. Déb.',
		'DEBIT_CARD-MXN':'T. Créd.',
		'CHECK-MXN':'Cheq.',
		'TRANSFER-MXN':'Transf.',
	};

	print: boolean = false;

	order_id:number | null = null;

	total_received:number = 0;

	ngOnInit(): void
	{
		this.subs.sink = this.route.paramMap.pipe
		(
			mergeMap((param_map:ParamMap)=>
			{
				this.payment_info = null;
				this.orders_by_id = {};

				this.print = param_map.get('print') != null;
				this.order_id = param_map.get('order_id') ? parseInt(param_map.get('order_id')) : null;
				return this.rest.payment_info.get( param_map.get('payment_id') )
			}),
			mergeMap((payment_info)=>
			{
				this.payment_info = payment_info;
				this.all_partial_payments = 0;

				if( !this.order_id)
				{
					this.order_id = payment_info.movements.reduce((prev:null|number,bm)=>{
						if( prev )
							return prev;
	
						return bm.bank_movement_orders.reduce((prev:null|number,bo:Bank_Movement_Order)=>{
							if( prev )
								return prev;
	
							return bo.order_id;
						},null);
					}, null);
	
				}

				let	payment_search:SearchObject<Payment> = this.getEmptySearch();
				payment_search.limit = 99999;
				payment_search.le.id = payment_info.payment.id;
				payment_search.sort_order = ['id_ASC'];
				payment_search.search_extra= { order_id: this.order_id };

				return forkJoin
				({
					payments: this.rest.payment_info.search( payment_search ) as Observable<RestResponse<PaymentInfo>>,
					order_info: this.rest.order_info.get( this.order_id )
				})
			})
		)
		.subscribe((response)=>
		{
			this.payment_info_list = response.payments.data as PaymentInfo[];
			console.log('Payment info list', this.payment_info_list);
			this.order_info = response.order_info;

			let descripcion:Bank_Movement_Order_Custom[] = [];

			let sum = 0;

			this.payment_info_list.forEach((payment_info)=>
			{
				if( payment_info.payment.id > this.payment_info.payment.id )
					return;

				payment_info.movements.forEach((movement)=>
				{
					movement.bank_movement_orders.forEach((bmo)=>
					{
						//filtrar los que correspondan a la orden
						if( bmo.order_id != this.order_info.order.id )
							return;

						sum += bmo.amount;
						let balance = this.order_info.order.total -(this.order_info.order.discount + sum);

						let payment_type:string = movement.bank_movement.transaction_type;

						let bmoc:Bank_Movement_Order_Custom = {
							...bmo,sum,balance, payment_type
						};

						descripcion.push(bmoc);
					});
				});
			});

			this.sums = descripcion;
			this.all_partial_payments = sum;
			if( this.print )
			{
				setTimeout(()=>{
					window.print();
					window.close();
				},200);
			}
		},(error)=>this.showError(error));
	}

	justPrint()
	{
		window.print();
	}

	async printUsbReceipt(evt: Event)
	{
		if( !this.payment_info || !this.order_info )
			return;

		try
		{
			const store_id = this.payment_info.store?.id || this.order_info.order.store_id;
			await ensurePosPrinterLoaded(this.rest, this.printerService, store_id);

			const printer = this.printerService.getPosPrinter();
			if( !printer )
			{
				this.showError('No hay impresora USB configurada');
				return;
			}

			const ticket = await resolveTicketForUsbPrint(this.rest, this.printerService, printer);
			const order = this.order_info.order;

			const data: SingleOrderReceiptData =
			{
				kind: 'SINGLE_ORDER',
				title: 'RECIBO DE ABONOS',
				store: storeSnapshot(this.payment_info.store || this.order_info.store),
				client_name: this.payment_info.paid_by_user?.name || this.order_info.client?.name || '',
				attendant_name: this.payment_info.created_by_user?.name || '',
				base_currency_id: order.currency_id,
				received_amount: this.payment_info.payment.received_amount || 0,
				change_amount: this.payment_info.payment.change_amount || 0,
				total_abonado: this.all_partial_payments,
				pendiente: Math.max(0, order.total - (order.discount + this.all_partial_payments)),
				order_id: order.id,
				folio: order.store_consecutive,
				subtotal: order.total,
				discount: order.discount,
				total: order.total - order.discount,
				abonos: this.sums
					.filter(bmo => bmo.order_id === order.id)
					.map(bmo => ({
						date: bmo.created || '',
						type_code: paymentTypeCode(bmo.payment_type, bmo.currency_id),
						amount: bmo.currency_amount || 0,
						currency_id: bmo.currency_id,
						balance: bmo.balance
					}))
			};

			const result = await this.printerService.printPaymentReceipt(data, 1, evt, ticket);

			if( result.success )
				this.showSuccess('Recibo impreso correctamente');
			else
				this.showError('Error al imprimir: ' + result.message);
		}
		catch (error: any)
		{
			this.showError('Error al imprimir via USB: ' + (error?.message || 'Error desconocido'));
		}
	}

	back()
	{
		this.location.back();
	}
}
